import _ from 'lodash'
import { useMemo } from 'react'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { useGetWaitingRoomQuery, useUpdateWaitingRoomMutation } from '@/core/services/waiting-room'
import { WaitingRoomFormData } from '@/features/waiting-room'
import { getApiErrorMessage } from '@/utils/errors'

export function useEditWaitingRoomForm(waitingRoomId: string) {
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const { data: waitingRoom } = useGetWaitingRoomQuery(waitingRoomId)
  const [updateWaitingRoom, { isLoading: isUpdating }] = useUpdateWaitingRoomMutation()

  const initialValues = useMemo(
    () => ({
      avgWaitingTime: waitingRoom?.avgWaitingTime ?? 15,
      description: waitingRoom?.description ?? '',
      doctors: waitingRoom?.doctors ?? [],
      establishments: waitingRoom?.establishments.map((establishment) => establishment._id) ?? [],
      isPrioritized: waitingRoom?.isPrioritized,
      name: waitingRoom?.name ?? '',
      openingHours:
        waitingRoom?.openingHours ??
        _.range(7).map((day) => ({
          day,
          periods: [],
        })),
      specialities: waitingRoom?.specialities ?? [],
      price: waitingRoom?.price ?? 0,
      paymentReceiverId: waitingRoom?.paymentReceiverId ?? '',
      allowPatientSelection: waitingRoom?.allowPatientSelection ?? false,
      paymentDialogConfig: waitingRoom?.paymentDialogConfig ?? { enabled: false, showForRoles: [] },
      fairDistribution: {
        enabled: waitingRoom?.fairDistribution?.enabled ?? false,
        maxConcurrent: waitingRoom?.fairDistribution?.maxConcurrent ?? 1,
        cooldownSeconds: waitingRoom?.fairDistribution?.cooldownSeconds ?? 60,
        // Legacy rooms predate these fields; default so the form (and its zod schema) stay valid.
        strategy: waitingRoom?.fairDistribution?.strategy ?? ('cap' as const),
        offerTtlSeconds: waitingRoom?.fairDistribution?.offerTtlSeconds ?? 30,
        prominentOffer: waitingRoom?.fairDistribution?.prominentOffer ?? false,
      },
      readinessConfig: {
        enabled: waitingRoom?.readinessConfig?.enabled ?? false,
        prioritizeReady: waitingRoom?.readinessConfig?.prioritizeReady ?? false,
        checklistTemplateId: waitingRoom?.readinessConfig?.checklistTemplateId ?? '',
      },
      coPresenceConfig: {
        enabled: waitingRoom?.coPresenceConfig?.enabled ?? false,
        allowAssign: waitingRoom?.coPresenceConfig?.allowAssign ?? false,
      },
    }),
    [waitingRoom]
  )

  const onSubmit = async (values: WaitingRoomFormData) => {
    if (!waitingRoomId) return
    if (values.paymentReceiverId === '') values.paymentReceiverId = undefined
    if (values.readinessConfig.checklistTemplateId === '') values.readinessConfig.checklistTemplateId = undefined
    try {
      await updateWaitingRoom({
        id: waitingRoomId,
        updates: {
          ...values,
          specialities: _.map(values.specialities, (speciality) => _.pick(speciality, ['code', 'name', 'selectByDefault'])),
        },
      }).unwrap()
      enqueueSnackbar(t('notification.waitingRoom.update.success'), { variant: 'success' })
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('notification.waitingRoom.update.error')), { variant: 'error' })
    }
  }

  return { initialValues, onSubmit, isSubmitting: isUpdating }
}
